import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { StatusPill } from '@/components/cccd/StatusPill';
import { inputCls, labelCls } from '@/components/admin/FormComponents';
import { CheckCircleIcon, RefreshIcon, AlertIcon } from '@/icons';
import {
  cccdScoring,
  type CategoryEvaluationParameter,
  type CategoryResultsGrid as CategoryResultsGridData,
  type CategoryResultsGridTeam,
  type SaveTeamResultInput,
} from '@/lib/api/cccdScoringClient';
import type { EventCategory } from '@/lib/api/cccdAdminClient';
import { toastError } from '@/lib/toast';
import { readActiveCategoryId, rememberActiveCategoryId } from '@/lib/cccdActiveCategory';
import { LoadState } from "@/components/ui/LoadState"

// Ítems de la rúbrica CCCD 2026 cuyo resultado es un tercio discreto
// (100/50/0), no un valor continuo — ver openspec/changes/
// cccd-2026-rubric-evaluation-parameters design.md D3.
const TIERED_PARAMETER_NAMES = ['Carga útil (huevo)', 'Despliegue del paracaídas', 'Estado final'];

type CellState = 'idle' | 'saving' | 'saved' | 'cleared' | 'error';
type SaveResultPayload = Omit<SaveTeamResultInput, 'parameterId'>;

function saveErrorMessage(err: any) {
  return err?.response?.data?.error ?? 'No se pudo guardar la celda.';
}

// Estado de guardado persistente por celda (cccd-scoring-results-entry):
// reemplaza el toast transitorio — "error" se queda visible hasta que el
// usuario reintente o guarde de nuevo con éxito, no desaparece solo.
function SaveIndicator({ state, onRetry }: { state: CellState; onRetry: () => void }) {
  if (state === 'saving') {
    return (
      <span className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
        <RefreshIcon className="size-3 animate-spin" /> Guardando…
      </span>
    );
  }
  if (state === 'saved') {
    return (
      <span className="mt-1.5 flex items-center gap-1 text-[11px] text-success-600 dark:text-success-400">
        <CheckCircleIcon className="size-3" /> Guardado
      </span>
    );
  }
  if (state === 'cleared') {
    return (
      <span className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300">
        <CheckCircleIcon className="size-3" /> Valor borrado
      </span>
    );
  }
  if (state === 'error') {
    return (
      <button
        type="button"
        onClick={onRetry}
        className="mt-1.5 flex min-h-11 items-center gap-1 text-left text-[11px] font-medium text-error-600 hover:text-error-700 hover:underline sm:min-h-0"
      >
        <AlertIcon className="size-3 shrink-0" /> Error — reintentar
      </button>
    );
  }
  return null;
}

function TieredCell({ value, onSave, label, disabled }: { value: number | null; onSave: (v: number | null) => void; label: string; disabled?: boolean }) {
  return (
    <div role="group" aria-label={label} className="flex w-[160px] gap-1 rounded-lg bg-gray-50 p-1 dark:bg-white/[0.03]">
      {[100, 50, 0].map((tier) => (
        <button
          key={tier}
          type="button"
          disabled={disabled}
          aria-pressed={value === tier}
          aria-label={`${label}: ${tier}`}
          title={value === tier ? 'Tocar de nuevo para borrar el valor' : undefined}
          onClick={() => onSave(value === tier ? null : tier)}
          className={`min-h-11 flex-1 rounded-md border px-2 py-1.5 text-[12px] font-semibold transition-colors disabled:cursor-wait disabled:opacity-60 sm:min-h-9 ${
            value === tier
              ? 'border-brand-500 bg-brand-500 text-white shadow-theme-xs'
              : 'border-transparent bg-white text-gray-500 hover:border-gray-200 hover:text-gray-700 dark:bg-white/[0.06] dark:text-gray-400 dark:hover:text-gray-200'
          }`}
        >
          {tier}
        </button>
      ))}
    </div>
  );
}

function NumericCell({ value, onSave, label }: { value: number | null; onSave: (v: number | null) => void; label: string }) {
  const [draft, setDraft] = useState(value?.toString() ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  // Si el servidor trae otro valor (otro juez, refresco), se refleja, salvo que se esté escribiendo en este campo.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setDraft(value?.toString() ?? '');
  }, [value]);
  return (
    <input
      ref={inputRef}
      type="number"
      inputMode="decimal"
      step="any"
      aria-label={label}
      className={`${inputCls} h-11 w-20 px-1.5 text-center sm:h-9`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        // El teclado numérico de iOS no trae "Listo": Enter confirma igual que salir del campo.
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      onBlur={() => {
        const next = draft === '' ? null : Number(draft);
        if (next !== value) onSave(next);
      }}
    />
  );
}

function YesNoCell({ value, onSave, label }: { value: number | null; onSave: (v: number | null) => void; label: string }) {
  return (
    <select
      aria-label={label}
      className={`${inputCls} h-11 w-20 px-1.5 sm:h-9`}
      value={value == null ? '' : String(value)}
      onChange={(e) => onSave(e.target.value === '' ? null : Number(e.target.value))}
    >
      <option value="">—</option>
      <option value="1">Sí</option>
      <option value="0">No</option>
    </select>
  );
}

// calculated_height necesita 3 ángulos + 3 ajustes, no un solo valor. Antes
// vivía en una fila expandible (workaround del "sin modales/popups en
// Gestión"); ahora los 6 campos están siempre visibles en línea dentro de
// la misma celda — sin paso de expandir/colapsar, y sin usar un overlay.
type HeightField = 'angleR1' | 'angleR2' | 'angleR3' | 'adjustmentA1' | 'adjustmentA2' | 'adjustmentA3';

function CalculatedHeightFields({ teamId, teamName, parameterId, onSave }: {
  teamId: string;
  teamName: string;
  parameterId: string;
  onSave: (payload: SaveResultPayload) => void;
}) {
  const idPrefix = useId();
  const focusedField = useRef<HeightField | null>(null);
  const { data } = useQuery({
    queryKey: ['team-results', teamId],
    queryFn: () => cccdScoring.getTeamResults(teamId),
  });
  const existing = data?.results.find((r) => r.parameterId === parameterId);

  const [values, setValues] = useState({
    angleR1: existing?.angleR1?.toString() ?? '',
    angleR2: existing?.angleR2?.toString() ?? '',
    angleR3: existing?.angleR3?.toString() ?? '',
    adjustmentA1: existing?.adjustmentA1?.toString() ?? '',
    adjustmentA2: existing?.adjustmentA2?.toString() ?? '',
    adjustmentA3: existing?.adjustmentA3?.toString() ?? '',
  });

  // Sincroniza con el servidor, pero nunca pisa el campo en el que se está escribiendo (la respuesta del
  // campo anterior llegaba y borraba lo tecleado en el siguiente).
  useEffect(() => {
    const fromServer: Record<HeightField, string> = {
      angleR1: existing?.angleR1?.toString() ?? '',
      angleR2: existing?.angleR2?.toString() ?? '',
      angleR3: existing?.angleR3?.toString() ?? '',
      adjustmentA1: existing?.adjustmentA1?.toString() ?? '',
      adjustmentA2: existing?.adjustmentA2?.toString() ?? '',
      adjustmentA3: existing?.adjustmentA3?.toString() ?? '',
    };
    setValues((prev) => {
      const next = { ...fromServer };
      if (focusedField.current) next[focusedField.current] = prev[focusedField.current];
      return next;
    });
  }, [existing?.angleR1, existing?.angleR2, existing?.angleR3, existing?.adjustmentA1, existing?.adjustmentA2, existing?.adjustmentA3]);

  const set = (key: keyof typeof values, v: string) => setValues((prev) => ({ ...prev, [key]: v }));

  function submit() {
    const payload = {
      angleR1: values.angleR1 !== '' ? Number(values.angleR1) : null,
      angleR2: values.angleR2 !== '' ? Number(values.angleR2) : null,
      angleR3: values.angleR3 !== '' ? Number(values.angleR3) : null,
      adjustmentA1: values.adjustmentA1 !== '' ? Number(values.adjustmentA1) : null,
      adjustmentA2: values.adjustmentA2 !== '' ? Number(values.adjustmentA2) : null,
      adjustmentA3: values.adjustmentA3 !== '' ? Number(values.adjustmentA3) : null,
    };
    // Pasar por los seis campos sin cambiar nada no debe disparar seis guardados (cada uno recalcula la categoría).
    const unchanged = (Object.keys(payload) as HeightField[]).every((k) => (payload[k] ?? null) === (existing?.[k] ?? null));
    if (unchanged) return;
    onSave(payload);
  }

  const filled = Object.values(values).filter((v) => v !== '').length;
  const fieldProps = (f: HeightField, name: string) => ({
    id: `${idPrefix}-${f}`,
    type: 'number' as const,
    inputMode: 'decimal' as const,
    step: 'any' as const,
    'aria-label': `${name} de ${teamName}`,
    className: `${inputCls} h-11 w-14 px-1 text-center text-[12px] sm:h-9`,
    value: values[f],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(f, e.target.value),
    onFocus: () => { focusedField.current = f; },
    onBlur: () => { focusedField.current = null; submit(); },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') e.currentTarget.blur(); },
  });

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-gray-25 p-2 dark:bg-white/[0.02]">
      <div>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Ángulos (°)</p>
        <div className="flex gap-1">
          {(['angleR1', 'angleR2', 'angleR3'] as const).map((f, i) => (
            <div key={f}>
              <label htmlFor={`${idPrefix}-${f}`} className={`${labelCls} text-[11px]`}>R{i + 1}</label>
              <input {...fieldProps(f, `Ángulo R${i + 1}`)} />
            </div>
          ))}
        </div>
      </div>
      <div>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Ajustes</p>
        <div className="flex gap-1">
          {(['adjustmentA1', 'adjustmentA2', 'adjustmentA3'] as const).map((f, i) => (
            <div key={f}>
              <label htmlFor={`${idPrefix}-${f}`} className={`${labelCls} text-[11px]`}>A{i + 1}</label>
              <input {...fieldProps(f, `Ajuste A${i + 1}`)} />
            </div>
          ))}
        </div>
      </div>
      {existing?.calculatedScore != null ? (
        <div className="flex items-center gap-1.5 border-t border-gray-100 pt-1.5 dark:border-gray-800">
          <span className="text-[11px] text-gray-500 dark:text-gray-400">Puntaje:</span>
          <StatusPill tone="brand" label={String(existing.calculatedScore)} />
        </div>
      ) : filled > 0 ? (
        // El puntaje es proporcional a las alturas de todos los equipos: sin el dato de alguno no se calcula.
        <p className="border-t border-gray-100 pt-1.5 text-[11px] text-gray-600 dark:border-gray-800 dark:text-gray-300">
          {filled === 6 ? 'Pendiente: faltan alturas de otros equipos' : 'Pendiente: completa ángulos y ajustes'}
        </p>
      ) : null}
    </div>
  );
}

function ParameterCell({ team, parameter, teamId, onSave, saving }: {
  team: CategoryResultsGridTeam;
  parameter: CategoryEvaluationParameter;
  teamId: string;
  onSave: (v: SaveResultPayload) => void;
  saving: boolean;
}) {
  const value = team.results[parameter.id] ?? null;
  const label = `${parameter.name} de ${team.name}`;

  if (parameter.parameterType === 'calculated_height') {
    return <CalculatedHeightFields teamId={teamId} teamName={team.name} parameterId={parameter.id} onSave={onSave} />;
  }
  if (parameter.parameterType === 'numeric_value' && TIERED_PARAMETER_NAMES.includes(parameter.name)) {
    return <TieredCell value={value} label={label} disabled={saving} onSave={(v) => onSave({ rawValue: v })} />;
  }
  if (parameter.parameterType === 'yes_no_parameter') {
    return <YesNoCell value={value} label={label} onSave={(v) => onSave({ rawValue: v })} />;
  }
  return <NumericCell value={value} label={label} onSave={(v) => onSave({ rawValue: v })} />;
}

function GridRow({ team, parameters, onCellSaved }: {
  team: CategoryResultsGridTeam;
  parameters: CategoryEvaluationParameter[];
  onCellSaved: (teamId: string, parameterId: string, value: number | null) => void;
}) {
  const [cellState, setCellState] = useState<Record<string, CellState>>({});
  const lastPayload = useRef<Record<string, SaveResultPayload>>({});
  // Secuencia por celda: si dos guardados de la misma celda se cruzan, solo cuenta la respuesta del último.
  const seq = useRef<Record<string, number>>({});
  const savedTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => () => {
    Object.values(savedTimers.current).forEach(clearTimeout);
  }, []);

  function save(parameterId: string, payload: SaveResultPayload) {
    lastPayload.current[parameterId] = payload;
    const mySeq = (seq.current[parameterId] ?? 0) + 1;
    seq.current[parameterId] = mySeq;
    setCellState((prev) => ({ ...prev, [parameterId]: 'saving' }));
    cccdScoring
      .saveTeamResult(team.id, { parameterId, ...payload })
      .then(() => {
        if (seq.current[parameterId] !== mySeq) return;
        const cleared = 'rawValue' in payload && payload.rawValue == null;
        setCellState((prev) => ({ ...prev, [parameterId]: cleared ? 'cleared' : 'saved' }));
        onCellSaved(team.id, parameterId, payload.rawValue ?? null);
        clearTimeout(savedTimers.current[parameterId]);
        savedTimers.current[parameterId] = setTimeout(() => {
          setCellState((prev) => (prev[parameterId] === 'saved' || prev[parameterId] === 'cleared' ? { ...prev, [parameterId]: 'idle' } : prev));
        }, 1500);
      })
      .catch((err) => {
        if (seq.current[parameterId] !== mySeq) return;
        setCellState((prev) => ({ ...prev, [parameterId]: 'error' }));
        toastError(saveErrorMessage(err));
      });
  }

  function retry(parameterId: string) {
    const payload = lastPayload.current[parameterId];
    if (payload) save(parameterId, payload);
  }

  return (
    <tr className="group border-t border-gray-100 transition-colors hover:bg-gray-50/70 dark:border-gray-800 dark:hover:bg-white/[0.03]">
      <td className="sticky left-0 z-10 border-r border-gray-200 bg-white px-3 py-3 transition-colors group-hover:bg-gray-50/70 dark:border-gray-800 dark:bg-gray-900 dark:group-hover:bg-white/[0.03]">
        <p className="text-[13px] font-medium text-gray-800 dark:text-white/90">{team.name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{team.institution?.name ?? 'Sin institución'}</p>
      </td>
      {parameters.map((p) => (
        <td key={p.id} className="px-3 py-3 align-top">
          <ParameterCell team={team} parameter={p} teamId={team.id} onSave={(payload) => save(p.id, payload)} saving={cellState[p.id] === 'saving'} />
          <SaveIndicator state={cellState[p.id] ?? 'idle'} onRetry={() => retry(p.id)} />
        </td>
      ))}
    </tr>
  );
}

// Grilla de captura tipo hoja de cálculo (cccd-scoring-spreadsheet-grid,
// rediseñada por cccd-scoring-ux-redesign) — una fila por equipo, una
// columna por parámetro vinculado, celda editable con guardado al perder
// foco. El orden de las filas es estable (por nombre de equipo) y nunca
// depende del ranking: el ranking/posición se ve en la pestaña
// "Clasificación" (ScoringLeaderboard), no aquí — ver design.md decisión 1.
export function ScoringResultsGrid({ categories }: {
  categories: EventCategory[];
}) {
  const qc = useQueryClient();
  const scopeId = categories[0]?.eventId ?? categories[0]?.regionalId;
  const [activeCategoryId, setActiveCategoryId] = useState(() => readActiveCategoryId(scopeId, categories));
  useEffect(() => {
    rememberActiveCategoryId(scopeId, activeCategoryId);
  }, [scopeId, activeCategoryId]);

  const gridKey = ['category-results-grid', activeCategoryId];
  const { data: grid, isLoading } = useQuery<CategoryResultsGridData>({
    queryKey: gridKey,
    queryFn: () => cccdScoring.getCategoryResultsGrid(activeCategoryId),
    enabled: !!activeCategoryId,
    // Varios jueces capturan a la vez: la grilla se refresca sola para ver lo que puso el otro.
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

  const sortedTeams = useMemo(
    () => (grid ? [...grid.teams].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [grid],
  );

  function handleCellSaved(teamId: string, parameterId: string, value: number | null) {
    qc.setQueryData<CategoryResultsGridData | undefined>(gridKey, (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        teams: prev.teams.map((t) => (t.id === teamId ? { ...t, results: { ...t.results, [parameterId]: value } } : t)),
      };
    });
    // El puntaje de altura es proporcional a todos los equipos: se refrescan los resultados de todos, no solo del editado.
    qc.invalidateQueries({ queryKey: ['team-results'] });
    qc.invalidateQueries({ queryKey: ['category-leaderboard', activeCategoryId] });
  }

  if (!categories.some((c) => c.id === activeCategoryId) && categories[0]) {
    setActiveCategoryId(categories[0].id);
  }

  return (
    <SectionHeader
      title="Resultados por equipo"
      subtitle="Escribe el valor de cada celda — se guarda al salir del campo. Para ver el ranking, revisa la pestaña Clasificación"
      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19V5M4 19h16M8 15l4-4 3 3 5-6" /></svg>}
    >
      {categories.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Este evento no tiene categorías configuradas todavía.</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategoryId(cat.id)}
                className={`min-h-11 rounded-lg px-3.5 py-1.5 text-[13px] font-medium transition-colors sm:min-h-0 ${
                  activeCategoryId === cat.id
                    ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/[0.12] dark:text-brand-400'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-300'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {isLoading ? (
            <LoadState />
          ) : !grid || grid.teams.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 dark:border-gray-700">
              Sin equipos en esta categoría.
            </p>
          ) : grid.parameters.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 dark:border-gray-700">
              Esta categoría no tiene parámetros vinculados — configúralos en Editar categoría.
            </p>
          ) : (
            <div className="-mx-6 max-h-[65vh] overflow-auto custom-scrollbar">
              <table className="w-full min-w-max text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="sticky left-0 top-0 z-30 border-r border-b border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-800 dark:bg-gray-900">Equipo</th>
                    {grid.parameters.map((p) => (
                      <th key={p.id} className="sticky top-0 z-20 border-b border-gray-200 bg-gray-50 px-3 py-2.5 font-semibold normal-case dark:border-gray-800 dark:bg-gray-900">{p.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedTeams.map((team) => (
                    <GridRow key={team.id} team={team} parameters={grid.parameters} onCellSaved={handleCellSaved} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </SectionHeader>
  );
}
